import Phaser from 'phaser';
import { findSheet, stampPages, SHEET_TILE_SIZE, type FurnitureCatalogEntry } from '@campus/core';

/**
 * Textures du mobilier (partagé ZoneScene / EditorScene) :
 * - objet du catalogue → son image (`entry.url`), texture `entry.id` ;
 * - objet animé → bande d'images chargée en spritesheet, texture `entry.id`,
 *   animation jouée en boucle (spawnFurnitureImage) ;
 * - tampon de planche hors catalogue (onglet « Planches », `url` vide) →
 *   pages de la planche chargées à la demande (seules celles que le tampon
 *   couvre). Tampon contenu dans une page et non retourné : simple frame
 *   nommée `entry.id` sur la texture de la page — aucune copie en mémoire
 *   graphique. Sinon (à cheval sur deux pages, ou miroir) : dessiné une fois
 *   dans une texture canvas `entry.id`.
 *
 * Toujours passer par furnitureTexture()/spawnFurnitureImage() pour afficher
 * un meuble, jamais par `scene.add.image(x, y, entry.id)`.
 */

export interface FurnitureTexture {
  key: string;
  frame?: string | number;
}

function sheetPageKey(slug: string, page: number): string {
  return `sheet:${slug}:${page}`;
}

/** Tampon affichable directement depuis sa page (pas de copie) ? */
function pageFrameOf(entry: FurnitureCatalogEntry): { key: string } | null {
  const stamp = entry.stamp;
  if (entry.url || !stamp || stamp.flipX) return null;
  const pages = stampPages(stamp);
  return pages.length === 1 ? { key: sheetPageKey(stamp.sheet, pages[0]!) } : null;
}

export function furnitureTexture(entry: FurnitureCatalogEntry): FurnitureTexture {
  if (entry.anim) return { key: entry.id, frame: 0 };
  const page = pageFrameOf(entry);
  if (page) return { key: page.key, frame: entry.id };
  return { key: entry.id };
}

export function hasFurnitureTexture(scene: Phaser.Scene, entry: FurnitureCatalogEntry): boolean {
  const t = furnitureTexture(entry);
  if (!scene.textures.exists(t.key)) return false;
  return typeof t.frame !== 'string' || scene.textures.get(t.key).has(t.frame);
}

function animKey(entry: FurnitureCatalogEntry): string {
  return `furniture-anim:${entry.id}`;
}

/**
 * Affiche un meuble dont la texture est prête (ensureFurnitureTextures), ancré
 * en bas à gauche en (x, y). Objet animé : sprite qui joue sa boucle.
 */
export function spawnFurnitureImage(scene: Phaser.Scene, entry: FurnitureCatalogEntry, x: number, y: number): Phaser.GameObjects.Image {
  const t = furnitureTexture(entry);
  if (!entry.anim) return scene.add.image(x, y, t.key, t.frame).setOrigin(0, 1);
  const key = animKey(entry);
  if (!scene.anims.exists(key)) {
    scene.anims.create({
      key,
      frames: scene.anims.generateFrameNumbers(entry.id, { start: 0, end: entry.anim.frames - 1 }),
      frameRate: entry.anim.frameRate,
      repeat: -1,
    });
  }
  return scene.add.sprite(x, y, t.key, 0).setOrigin(0, 1).play(key);
}

/** Charge (si besoin) les textures de ces meubles. */
export function ensureFurnitureTextures(scene: Phaser.Scene, entries: FurnitureCatalogEntry[]): Promise<void> {
  const images = new Map<string, string>();
  const sheets = new Map<string, { url: string; frameWidth: number; frameHeight: number }>();
  const stamps: FurnitureCatalogEntry[] = [];
  for (const entry of entries) {
    if (hasFurnitureTexture(scene, entry)) continue;
    if (entry.anim) {
      sheets.set(entry.id, { url: entry.url, frameWidth: entry.anim.frameWidth, frameHeight: entry.anim.frameHeight });
    } else if (entry.url) {
      images.set(entry.id, entry.url);
    } else if (entry.stamp) {
      const sheet = findSheet(entry.stamp.sheet);
      if (!sheet) continue;
      stamps.push(entry);
      for (const p of stampPages(entry.stamp)) {
        const key = sheetPageKey(sheet.slug, p);
        if (!scene.textures.exists(key)) images.set(key, sheet.pages[p]!);
      }
    }
  }
  return loadFiles(scene, images, sheets).then(() => {
    for (const entry of stamps) buildStampTexture(scene, entry);
  });
}

/** Résout quand chaque fichier est chargé ou en échec (un fichier manquant ne bloque pas les autres). */
function loadFiles(
  scene: Phaser.Scene,
  images: Map<string, string>,
  sheets: Map<string, { url: string; frameWidth: number; frameHeight: number }>,
): Promise<void> {
  if (images.size === 0 && sheets.size === 0) return Promise.resolve();
  return new Promise((resolve) => {
    const pending = new Set([...images.keys(), ...sheets.keys()]);
    const settle = (key: string) => {
      if (!pending.delete(key)) return;
      if (pending.size > 0) return;
      scene.load.off(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
      resolve();
    };
    const onError = (file: Phaser.Loader.File) => settle(file.key);
    scene.load.on(Phaser.Loader.Events.FILE_LOAD_ERROR, onError);
    // Même clé déjà en file (appel concurrent) : Phaser ne la recharge pas,
    // mais l'événement de fin est émis une fois pour tous les écouteurs.
    for (const [key, url] of images) {
      scene.load.once(`filecomplete-image-${key}`, () => settle(key));
      scene.load.image(key, url);
    }
    for (const [key, s] of sheets) {
      scene.load.once(`filecomplete-spritesheet-${key}`, () => settle(key));
      scene.load.spritesheet(key, s.url, { frameWidth: s.frameWidth, frameHeight: s.frameHeight });
    }
    scene.load.start();
  });
}

function buildStampTexture(scene: Phaser.Scene, entry: FurnitureCatalogEntry): void {
  const stamp = entry.stamp!;
  const sheet = findSheet(stamp.sheet)!;
  const T = SHEET_TILE_SIZE;
  const width = stamp.tw * T;

  const page = pageFrameOf(entry);
  if (page) {
    const texture = scene.textures.exists(page.key) ? scene.textures.get(page.key) : null;
    if (texture && !texture.has(entry.id)) {
      const pageTop = Math.floor(stamp.ty / sheet.pageRows) * sheet.pageRows;
      texture.add(entry.id, 0, stamp.tx * T, (stamp.ty - pageTop) * T, width, stamp.th * T);
    }
    return;
  }

  if (scene.textures.exists(entry.id)) return;
  const canvas = scene.textures.createCanvas(entry.id, width, stamp.th * T);
  if (!canvas) return;
  const ctx = canvas.getContext();
  ctx.imageSmoothingEnabled = false;
  if (stamp.flipX) {
    ctx.translate(width, 0);
    ctx.scale(-1, 1);
  }
  for (const p of stampPages(stamp)) {
    const key = sheetPageKey(sheet.slug, p);
    if (!scene.textures.exists(key)) continue;
    const image = scene.textures.get(key).getSourceImage() as CanvasImageSource;
    const pageTop = p * sheet.pageRows;
    const y0 = Math.max(stamp.ty, pageTop);
    const y1 = Math.min(stamp.ty + stamp.th, pageTop + sheet.pageRows);
    ctx.drawImage(
      image,
      stamp.tx * T, (y0 - pageTop) * T, width, (y1 - y0) * T,
      0, (y0 - stamp.ty) * T, width, (y1 - y0) * T,
    );
  }
  canvas.refresh();
}
